import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Calendar, CheckSquare, Hash, Flame, Clock, ArrowRight, Briefcase, ListTodo, CalendarDays, ClipboardList, Tag, Settings, Plus, Check } from 'lucide-react';
import { AuroraGlassPanel } from '@easyflow/ui-frontend/components/ui/aurora-glass-panel';
import { calendarFeedEventsResponseSchema } from '@easyflow/shared';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import type { CalendarFeedEvent, FeedTopicListItem, FeedPopularPost, FeedTrendingHashtag, FeedMyTasksSummary } from '@easyflow/shared';

// DTO condivisi (`@easyflow/shared`): la lista topic arriva da `GET /feed/topics`
// (`is_following`), popolari/hashtag da `/feed/popular` e `/feed/hashtags/trending`.
type SidebarTopic = Pick<FeedTopicListItem, 'id' | 'slug' | 'name' | 'color' | 'is_following'>;
type SidebarPopularPost = FeedPopularPost;
type SidebarTrendingTag = FeedTrendingHashtag;

interface FeedSidebarProps {
  tenantSlug: string;
  topics?: SidebarTopic[];
  isModerator?: boolean;
  onToggleFollowTopic?: (topicId: string, follow: boolean) => void;
  onSelectTopic?: (topicId: string) => void;
  selectedTopicId?: string;
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500',
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const DAY_COLORS: Record<string, string> = {
  'Lun': 'bg-blue-500',
  'Mar': 'bg-emerald-500',
  'Mer': 'bg-purple-500',
  'Gio': 'bg-orange-500',
  'Ven': 'bg-pink-500',
  'Sab': 'bg-teal-500',
  'Dom': 'bg-red-500',
};

const WEEKDAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

export default function FeedSidebar({ tenantSlug, topics = [], isModerator = false, onToggleFollowTopic, onSelectTopic, selectedTopicId }: FeedSidebarProps) {
  const { user } = useAuth();
  const [popularPosts, setPopularPosts] = useState<SidebarPopularPost[]>([]);
  const [trendingTags, setTrendingTags] = useState<SidebarTrendingTag[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarFeedEvent[]>([]);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [myTasks, setMyTasks] = useState<FeedMyTasksSummary>({ total: 0, overdue: 0, today: 0, in_progress: 0, delegated: 0, following: 0 });

  useEffect(() => {
    if (!user) return;

    apiClient.get<FeedPopularPost[]>('/messenger/feed/popular')
      .then(d => { if (d.success) setPopularPosts(d.data || []); })
      .catch(() => {});

    apiClient.get<FeedTrendingHashtag[]>('/messenger/feed/hashtags/trending')
      .then(d => { if (d.success) setTrendingTags(d.data || []); })
      .catch(() => {});

    apiClient.get('/calendar/events?limit=5&upcoming=true')
      .then((body: unknown) => {
        const result = calendarFeedEventsResponseSchema.parse(body);
        setUpcomingEvents(result.data.slice(0, 5));
        setEventsError(null);
      })
      .catch(() => setEventsError('Impossibile caricare gli eventi'));

    apiClient.get<FeedMyTasksSummary>('/tasks/my-summary')
      .then(d => {
        if (d.success && d.data) {
          const t = d.data;
          setMyTasks({
            total: t.total || 0,
            overdue: t.overdue || 0,
            today: t.today || 0,
            in_progress: t.in_progress || 0,
            delegated: t.delegated || 0,
            following: t.following || 0,
          });
        }
      })
      .catch(() => {});
  }, [user]);

  return (
    <aside role="complementary" aria-label="Pannello laterale Feed" className="space-y-4">
      <AuroraGlassPanel as="section" intensity="medium" className="rounded-xl overflow-hidden p-0">
        <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border-b">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <Tag className="h-4 w-4 text-indigo-500" />
            </div>
            <h3 className="font-semibold text-sm">Topic</h3>
            {isModerator && (
              <Link
                to={`/${tenantSlug}/messenger/admin/topics`}
                className="ml-auto inline-flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                title="Gestisci topic"
              >
                <Settings className="h-3 w-3" /> Gestisci
              </Link>
            )}
          </div>
        </div>
        <div className="p-3 space-y-1">
          {topics.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">Nessun topic configurato</p>
          ) : (
            topics.map(t => {
              const isSelected = selectedTopicId === t.id;
              return (
                <div
                  key={t.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                >
                  <button
                    onClick={() => onSelectTopic?.(isSelected ? '' : t.id)}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left"
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: t.color || '#6366f1' }}
                    />
                    <span className="text-xs font-medium truncate">{t.name}</span>
                  </button>
                  {onToggleFollowTopic && (
                    <button
                      onClick={() => onToggleFollowTopic(t.id, !t.is_following)}
                      className={`shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-md transition-colors ${
                        t.is_following
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700'
                      }`}
                      title={t.is_following ? 'Smetti di seguire' : 'Segui topic'}
                    >
                      {t.is_following ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              );
            })
          )}
          {isModerator && (
            <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-1">
              <Link
                to={`/${tenantSlug}/messenger/admin/moderation`}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
              >
                <Flame className="h-3 w-3" /> Coda moderazione
              </Link>
              <Link
                to={`/${tenantSlug}/messenger/admin/audit`}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <ClipboardList className="h-3 w-3" /> Audit log
              </Link>
            </div>
          )}
        </div>
      </AuroraGlassPanel>

      <AuroraGlassPanel as="section" intensity="subtle" className="rounded-xl overflow-hidden p-0">
        <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-b">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
              <Flame className="h-4 w-4 text-orange-500" />
            </div>
            <h3 className="font-semibold text-sm">Post Popolari</h3>
          </div>
        </div>
        <div className="p-3">
          {popularPosts.length === 0 ? (
            <div className="text-center py-4">
              <TrendingUp className="h-8 w-8 mx-auto text-gray-200 dark:text-gray-700 mb-2" />
              <p className="text-xs text-gray-400">Nessun post popolare questa settimana</p>
            </div>
          ) : (
            <div className="space-y-1">
              {popularPosts.map((post) => {
                const authorName = `${post.author_first_name || ''} ${post.author_last_name || ''}`.trim();
                return (
                  <div key={post.id} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors group">
                    <div className={`h-7 w-7 rounded-full ${getAvatarColor(authorName)} flex items-center justify-center text-white text-[10px] font-bold shrink-0`}>
                      {(post.author_first_name || 'U')[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {post.title || 'Post'}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {authorName || 'Utente'} · {post.reaction_count || 0} reazioni · {post.comment_count || 0} commenti
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AuroraGlassPanel>

      {trendingTags.length > 0 && (
        <AuroraGlassPanel as="section" intensity="subtle" className="rounded-xl overflow-hidden p-0">
          <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-b">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Hash className="h-4 w-4 text-blue-500" />
              </div>
              <h3 className="font-semibold text-sm">Hashtag di Tendenza</h3>
            </div>
          </div>
          <div className="p-3">
            <div className="flex flex-wrap gap-1.5">
              {trendingTags.map((tag) => {
                const size = Math.min(Math.max(Number(tag.post_count) || 1, 1), 5);
                return (
                  <span
                    key={tag.tag}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs cursor-pointer hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-900/40 dark:hover:to-indigo-900/40 transition-colors border border-blue-100 dark:border-blue-800/50"
                    style={{ fontSize: `${10 + size}px` }}
                  >
                    <Hash className="h-3 w-3" />
                    {tag.tag}
                    <span className="text-blue-400 dark:text-blue-500 font-semibold text-[10px]">{tag.post_count}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </AuroraGlassPanel>
      )}

      <AuroraGlassPanel as="section" intensity="subtle" className="rounded-xl overflow-hidden p-0">
          <div className="px-4 py-3 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-b">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
                <ClipboardList className="h-4 w-4 text-violet-500" />
              </div>
              <h3 className="font-semibold text-sm">I Miei Incarichi</h3>
              <span className="ml-auto text-xs font-bold text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/50 px-2 py-0.5 rounded-full">{myTasks.total}</span>
            </div>
          </div>
          <div className="p-3 space-y-1.5">
            {myTasks.in_progress > 0 && (
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-xs text-gray-600 dark:text-gray-400">In corso</span>
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{myTasks.in_progress}</span>
              </div>
            )}
            {myTasks.today > 0 && (
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">In scadenza oggi</span>
                <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{myTasks.today}</span>
              </div>
            )}
            {myTasks.overdue > 0 && (
              <div className="flex items-center justify-between px-2 py-1 bg-red-50 dark:bg-red-900/10 rounded-lg">
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">In ritardo</span>
                <span className="text-sm font-bold text-red-600 dark:text-red-400">{myTasks.overdue}</span>
              </div>
            )}
            {myTasks.delegated > 0 && (
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-xs text-gray-500">Impostati da me</span>
                <span className="text-sm font-bold text-gray-600 dark:text-gray-400">{myTasks.delegated}</span>
              </div>
            )}
            {myTasks.following > 0 && (
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-xs text-gray-500">Following</span>
                <span className="text-sm font-bold text-gray-600 dark:text-gray-400">{myTasks.following}</span>
              </div>
            )}
            {myTasks.total === 0 && (
              <div className="text-center py-2">
                <p className="text-xs text-gray-400">Nessun incarico assegnato</p>
              </div>
            )}
            <Link to={`/${tenantSlug}/tasks`} className="flex items-center justify-center gap-1.5 px-3 py-1.5 mt-1 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 text-xs font-medium hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors">
              Vedi tutti gli incarichi <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
      </AuroraGlassPanel>

      <AuroraGlassPanel as="section" intensity="subtle" className="rounded-xl overflow-hidden p-0">
          <div className="px-4 py-3 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-950/30 dark:to-teal-950/30 border-b">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center">
                <CalendarDays className="h-4 w-4 text-cyan-500" />
              </div>
              <h3 className="font-semibold text-sm">Eventi Futuri</h3>
            </div>
          </div>
          <div className="p-3 space-y-1.5">
            {eventsError && <p role="alert" className="text-xs text-red-500 text-center py-3">{eventsError}</p>}
            {!eventsError && upcomingEvents.length === 0 && (
              <div className="text-center py-3">
                <CalendarDays className="h-6 w-6 mx-auto text-gray-200 dark:text-gray-700 mb-1" />
                <p className="text-xs text-gray-400">Nessun evento in programma</p>
              </div>
            )}
            {upcomingEvents.map((ev) => {
              const evDate = new Date(ev.start);
              const dayOfWeek = WEEKDAY_SHORT[evDate.getDay()];
              return (
                <div key={ev.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors group">
                  <div className="text-center min-w-[36px]">
                    <div className="text-sm font-bold text-gray-800 dark:text-gray-200">{evDate.getDate()}</div>
                    <div className="text-[9px] text-gray-400 uppercase font-medium">{dayOfWeek}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                      {ev.title}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {evDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <Link to={`/${tenantSlug}/calendar`} className="flex items-center justify-center gap-1.5 px-3 py-1.5 mt-1 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 text-xs font-medium hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors">
              Vedi calendario <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
      </AuroraGlassPanel>

      <AuroraGlassPanel as="section" intensity="subtle" className="rounded-xl overflow-hidden p-0">
        <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-b">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
              <ListTodo className="h-4 w-4 text-emerald-500" />
            </div>
            <h3 className="font-semibold text-sm">Link Rapidi</h3>
          </div>
        </div>
        <div className="p-2">
          <Link to={`/${tenantSlug}/messenger/chat`} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 text-sm text-gray-700 dark:text-gray-300 transition-colors group">
            <div className="h-6 w-6 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Briefcase className="h-3.5 w-3.5 text-blue-500" />
            </div>
            <span>Chat</span>
            <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-gray-400" />
          </Link>
          <Link to={`/${tenantSlug}/messenger/canali`} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 text-sm text-gray-700 dark:text-gray-300 transition-colors group">
            <div className="h-6 w-6 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Hash className="h-3.5 w-3.5 text-purple-500" />
            </div>
            <span>Canali</span>
            <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-gray-400" />
          </Link>
        </div>
      </AuroraGlassPanel>
    </aside>
  );
}
